import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { CSSResultGroup, html, LitElement, nothing, TemplateResult } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { BasketListItem } from 'components/ordering/multiple-baskets/basket-store/basket-list-parser.js'
import { style } from './basket-selector-basket.style.js'
import { SelectingBasketEvent } from 'components/ordering/multiple-baskets/basket-events.js'
import { renderDeliveryPolicyBadge } from 'components/ordering/multiple-baskets/delivery-policy.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { WINDOW_LOCATION, WindowLocation } from '@cloudsuite/mosaic-shared/scripts/window-location.js'
import 'components/ordering/multiple-baskets/basket-selector/basket-delete-button.js'


initializeIconsByRole({
  'csc-basket-selector-basket.view': { name: 'eye', style: 'regular' },
})

@customElement('csc-basket-selector-basket')
export class CSCBasketSelectorBasket extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ attribute: 'basket' })
  basket!: BasketListItem

  @property({ attribute: 'ordering-endpoint' })
  orderingEndpoint!: string

  @property({ attribute: 'show-cumulated-count', type: Boolean })
  showCumulatedCount = false

  @property({ attribute: 'basket-index-url' })
  basketIndexUrl = ''

  @inject(WINDOW_LOCATION)
  private windowLocation!: WindowLocation

  private selectBasket = () => {
    this.dispatchEvent(new SelectingBasketEvent(this.basket))
  }

  private redirectToBasket() {
    this.windowLocation.redirect(new URL(this.basketIndexUrl, window.location.href))
  }

  private renderButton = () => {
    if (this.basket.selectedState === 'selected') {
      return html`
        <cse-button variant="secondary" size="small" @click="${this.redirectToBasket}">
          <cse-icon icon-role="csc-basket-selector-basket.view"></cse-icon>
          ${t('open_basket_button', { ns: 'csc-basket-selector-basket', default: 'View content' })}
        </cse-button>
      `
    }

    return html`
      <cse-button variant="secondary" size="small" @click="${this.selectBasket}">
        ${t('activate_basket_button', { ns: 'csc-basket-selector-basket', default: 'Activate' })}
      </cse-button>
    `
  }

  private renderDeleteButton = (): TemplateResult => {
    return html`
      <csc-basket-delete-button
        basket-id="${this.basket.basketId}"
        ordering-endpoint="${this.orderingEndpoint}">
      </csc-basket-delete-button>
    `
  }

  protected override render(): TemplateResult {
    const hasName = this.basket.name !== null
    const basketCount = this.showCumulatedCount ? this.basket.cumulatedCount : this.basket.lineCount

    return html`
      <div class="basket">
        <div class="row-space-between">
          <div class="column">
            ${renderDeliveryPolicyBadge(this.basket.deliveryPolicy?.name || null)}
            <cse-text variant="heading-6" class="basket-name">
              ${hasName
                ? this.basket.name
                : t('basket_name_fallback', { ns: 'csc-basket-selector-basket', default: 'Basket' })}
            </cse-text>
            <cse-text variant="caption-m" class="basket-count">
              ${t('basket_count', { ns: 'csc-basket-selector-basket', count: basketCount, default: '{{count}} product', default_plural: '{{count}} products' })}
            </cse-text>
          </div>
          <div class="column">
            ${this.basket.selectedState === 'selected'
              ? nothing
              : this.renderDeleteButton()}
          </div>
        </div>
        <div class="row-content-end">
          ${this.renderButton()}
        </div>
      </div>
    `
  }
}
